import { X } from "lucide-react";
import { motion } from "framer-motion";
import { useMemo } from "react";
import { useTranslation } from "@/packages/utils";
import { useFilter } from "./FilterContext";
import type { ActiveFilter } from "./types";
import { OPERATOR_LABELS } from "./types";

interface FilterPillProps {
  filter: ActiveFilter;
}

export function FilterPill({ filter }: FilterPillProps) {
  const { t } = useTranslation(["common"]);
  const { categoryGroups, removeFilter, setStep } = useFilter();

  const category = useMemo(() => {
    for (const group of categoryGroups) {
      const found = group.categories.find((c) => c.id === filter.categoryId);
      if (found) return found;
    }
    return undefined;
  }, [categoryGroups, filter.categoryId]);

  if (!category) return null;

  const valueLabels = filter.valueIds
    .map((vid) => category.values.find((v) => v.id === vid)?.label)
    .filter(Boolean)
    .join(", ");

  const operatorLabel = OPERATOR_LABELS[filter.operator];

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.9 }}
      transition={{ duration: 0.15 }}
      className="flex items-center gap-1 rounded-full bg-card border border-border text-[12px] text-secondary pl-2.5 pr-1 py-1 cursor-pointer hover:border-muted transition-colors"
    >
      <button
        type="button"
        onClick={() => setStep({ type: "editing_pill", filterId: filter.id })}
        className="flex items-center gap-1"
      >
        <span className="font-medium text-primary">{category.label}</span>
        <span className="text-muted">{operatorLabel}</span>
        <span>{valueLabels}</span>
      </button>
      <button
        type="button"
        aria-label={t("ui.filter.removeFilter")}
        onClick={(e) => {
          e.stopPropagation();
          removeFilter(filter.id);
        }}
        className="ml-0.5 flex h-4 w-4 items-center justify-center rounded-full hover:bg-card-hover text-muted hover:text-primary transition-colors cursor-pointer"
      >
        <X className="h-3 w-3" strokeWidth={2} />
      </button>
    </motion.div>
  );
}
